import tag from '@123/druid/dist/Framework/Decorators/Tag';
import type DelegateEvent from '@123/druid/dist/Types/DelegateEvent';
import InputDropdownUI from '@Component/Input/InputDropdown/InputDropdownUI';
import debounce from '@123/druid/dist/Utility/Decorator/Debounce';

@tag('dr-input-dropdown')
export default class InputDropdown extends HTMLElement {
    declare private ui: InputDropdownUI;

    // The threshold value at which the switch from and to dropdown occurs
    get threshold(): number {
        return this.attributeMap().getInt('threshold', -1);
    }

    set threshold(amount: number) {
        this.attributeMap().setInt('threshold', amount);
    }

    get maxlength(): number {
        return this.attributeMap().getInt('maxlength', -1);
    }

    set maxlength(amount: number) {
        this.attributeMap().setInt('maxlength', amount);
    }

    get value(): string {
        return this.ui.input.value;
    }

    set value(amount: string) {
        this.ui.input.value = amount;
        this.updateLayout();
    }

    get inputType(): string {
        return this.datamap().getString('inputType', 'manual');
    }

    set inputType(type: string) {
        this.datamap().setString('inputType', type);
    }

    public connectedCallback(): void {
        this.ui = new InputDropdownUI(this);
        this.updateLayout();

        this.ui.input.addEventListener('click.input', (event: Event) => {
            if (this.inputType !== 'dropdown') {
                // Prevent dropdown to open
                event.stopImmediatePropagation();
            }
        });
        this.ui.input.addEventListener('input.input', (event: Event) => this.onInputChange(event));
        this.ui.input.addEventListener('blur.input', () => {
            this.updateLayout();
            this.onInputBlur();
        });
        this.ui.input.addEventListener('keydown.input keyup.input ', (event) => event.stopImmediatePropagation());
        this.ui.input.addEventListener('focus', (event) => {
            if (this.ui.input.readOnly) {
                this.ui.input.blur();
                event.preventDefault();
                event.stopImmediatePropagation();
            }
        });
        this.ui.dropdown.addDelegateListener('click.option', '[data-role="option"]', (event: DelegateEvent) => {
            this.value = event.delegateTarget.attributeMap().getString('value');
            this.dispatchEvent(new CustomEvent('change', {bubbles: true, cancelable: true}));
        });
        this.ui.dropdown.addDelegateListener('click.option', '[data-role="option-more"]', () => {
            this.value = '';
            this.focusOnInput();
        });
    }

    public disconnectedCallback(): void {
        this.ui.input.removeEventListeners('click.input input.input blur.input keydown.input keyup.input');
        this.ui.dropdown.removeEventListeners('click.option');
    }

    @debounce(60)
    private onInputChange(event: Event): void {
        event.stopImmediatePropagation();

        if (this.maxlength !== -1) {
            this.ui.input.value = this.ui.input.value.substring(0, this.maxlength);
        }

        if (this.inputType === 'dropdown') {
            this.dispatchEvent(new CustomEvent('change', {bubbles: true, cancelable: true}));
        }
    }

    private onInputBlur(): void {
        if (this.inputType === 'manual') {
            this.dispatchEvent(new CustomEvent('change', {bubbles: true, cancelable: true}));
        }
    }

    /** Toggle between input or dropdown */
    private updateLayout(): void {
        if (document.activeElement === this.ui.input) {
            return;
        }

        const isDropdown = parseInt(this.value) <= this.threshold;
        this.inputType = isDropdown ? 'dropdown' : 'manual';

        if (this.inputType === 'dropdown' && this.ui.input.disabled === false) {
            this.ui.input.classList.add('c-input-dropdown__input--chevron');
        } else {
            this.ui.input.classList.remove('c-input-dropdown__input--chevron');
        }

        this.ui.input.readOnly = isDropdown;
    }

    private focusOnInput(): void {
        /** Programmatic focus using element.focus() doesn't trigger IOS system-level keyboard */
        /** Create temp input to force IOS keyboard to open */
        const tmpElement = document.createElement('input');
        tmpElement.style.border = '0';
        tmpElement.style.height = '0';
        tmpElement.style.margin = '0';
        tmpElement.style.padding = '0';
        tmpElement.style.opacity = '0';
        tmpElement.style.width = '0';
        this.appendChild(tmpElement);

        tmpElement.focus();
        this.ui.input.focus();

        this.removeChild(tmpElement);
    }
}
